import { type CSSProperties } from 'react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import Link, { type LinkProps } from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { ChevronLeftIcon, CoinStackFillIcon, CouponFillIcon, LogoutIcon, PenFillIcon, PersonStackFillIcon } from '@/icons';
import { isSidebarMenuActive, type SidebarMenu } from '@/layout/SidebarLayout/Sidebar';
import { useUser } from '@/lib/auth';
import { PRODUCT_STICKY_HEIGHT_CLASS } from '@/module/public/layout/productLayoutConstants';
import { Button } from '@/ui_v3';
import { formatEnglishTitleCase } from '@/utils/formatEnglishTitleCase';
import { cn } from '@/utils/tailwind.util';
export const ProfileSidebar = ({
  className,
  style
}: {
  className?: string;
  style?: CSSProperties;
}) => {
  const {
    menus,
    footerMenus
  } = useProfileSidebarMenu();
  return <aside style={style} className={cn(PRODUCT_STICKY_HEIGHT_CLASS, 'sticky w-[260px] shrink-0 overflow-hidden bg-default pb-[40px] pl-[40px] pr-[16px] pt-[12px]', className)} data-sentry-component="ProfileSidebar" data-sentry-source-file="ProfileSidebar.tsx">
      <nav className="flex h-full w-full flex-col justify-between">
        <div className="flex flex-col gap-xs">
          <ProfileSidebarHomeButton data-sentry-element="ProfileSidebarHomeButton" data-sentry-source-file="ProfileSidebar.tsx" />
          <div className="h-px w-full border-t border-alternative" />
          <div className="flex flex-col gap-xs">
            {menus.map(menu => <ProfileSidebarMenuItem key={menu.label} menu={menu} />)}
          </div>
        </div>
        <div className="flex flex-col">
          {footerMenus.map(menu => <ProfileSidebarFooterButton key={menu.label} menu={menu} />)}
        </div>
      </nav>
    </aside>;
};
export const useProfileSidebarMenu = () => {
  const {
    t,
    i18n
  } = useTranslation(['common']);
  const {
    logOut,
    user
  } = useUser();
  const isWorkspaceOwner = user?.workspace?.isPaidOwner ?? false;
  const formatLabel = (label: string) => formatEnglishTitleCase(label, i18n.resolvedLanguage);
  const profileEditMenu: SidebarMenu = {
    label: formatLabel(t('common:detailProfile')),
    href: '/profile/edit',
    target: '_self',
    icon: PenFillIcon,
    onClick: () => {
      logger.track('click_profile_edit');
    }
  };
  const voucherMenu: SidebarMenu = {
    label: formatLabel(t('common:voucher.title')),
    href: '/voucher',
    target: '_self',
    icon: CouponFillIcon,
    onClick: () => {
      logger.track('click_voucher');
    }
  };
  const pointMenu: SidebarMenu = {
    label: formatLabel(t('common:myPoints')),
    // `from` 은 도착 페이지의 진입 경로 트래킹용(quiz_point_history_view.place).
    href: '/profile/points?from=profile_sidebar',
    activeHrefs: ['/profile/points'],
    target: '_self',
    icon: CoinStackFillIcon,
    onClick: () => {}
  };
  const planMemberMenu: SidebarMenu = {
    label: formatLabel(t('common.plan.memberManage')),
    href: '/profile/plan',
    target: '_self',
    icon: PersonStackFillIcon,
    onClick: () => {
      logger.track('click_plan_member_manage');
    }
  };
  const footerMenus: SidebarMenu[] = [{
    label: t('common:header.logout'),
    href: null,
    target: null,
    onClick: () => {
      void logOut({
        redirectTo: '/landing'
      });
      logger.track('enter_logout', {
        location: 'profile'
      });
    },
    icon: LogoutIcon
  }];
  return {
    menus: [profileEditMenu, ...(user?.type?.toLowerCase() === 'student' ? [pointMenu] : []), voucherMenu, ...(isWorkspaceOwner ? [planMemberMenu] : [])],
    footerMenus
  };
};
const ProfileSidebarMenuItem = ({
  menu
}: {
  menu: SidebarMenu;
}) => {
  const {
    pathname
  } = useRouter();
  const isActive = isSidebarMenuActive(pathname, menu.href, menu.activeHrefs);
  const Icon = isActive && menu.activeIcon ? menu.activeIcon : menu.icon;
  const className = cn('flex h-[46px] min-h-[46px] w-full shrink-0 items-center gap-xs overflow-hidden rounded-sm pl-sm pr-md py-xs text-left text-body-md-semibold', 'justify-start hover:bg-neutral', isActive ? 'bg-neutral text-strong' : 'text-neutral');
  const content = <>
      <Icon size="sm" fill={isActive ? ICON_COLOR.strong : ICON_COLOR.alternative} className="shrink-0" />
      <span className="min-w-0 truncate">{menu.label}</span>
    </>;
  if (menu.href) {
    return <Link href={menu.href as LinkProps['href']} target={menu.target ?? '_self'} className={className} onClick={menu.onClick}>
        {content}
      </Link>;
  }
  return <button type="button" className={className} onClick={event => {
    event.preventDefault();
    menu.onClick();
  }} data-sentry-component="ProfileSidebarMenuItem" data-sentry-source-file="ProfileSidebar.tsx">
      {content}
    </button>;
};
const ProfileSidebarHomeButton = () => {
  const {
    t
  } = useTranslation('common');
  return <Button variant="transparent" intent="neutral" size="sm" className="w-full justify-start gap-3xs px-sm" asChild data-sentry-element="Button" data-sentry-component="ProfileSidebarHomeButton" data-sentry-source-file="ProfileSidebar.tsx">
      <Link href="/public" onClick={() => {
      logger.track('click_profile_sidebar_home');
    }} data-sentry-element="Link" data-sentry-source-file="ProfileSidebar.tsx">
        <ChevronLeftIcon size="sm" fill={ICON_COLOR.neutral} className="shrink-0" data-sentry-element="ChevronLeftIcon" data-sentry-source-file="ProfileSidebar.tsx" />
        <span className="min-w-0 flex-1 truncate text-left">
          {t('duplicateLogin.button.home')}
        </span>
      </Link>
    </Button>;
};
const ProfileSidebarFooterButton = ({
  menu
}: {
  menu: SidebarMenu;
}) => {
  const Icon = menu.icon;
  return <Button type="button" variant="transparent" intent="neutral" size="sm" className="w-full justify-start gap-3xs px-sm" onClick={event => {
    event.preventDefault();
    menu.onClick();
  }} data-sentry-element="Button" data-sentry-component="ProfileSidebarFooterButton" data-sentry-source-file="ProfileSidebar.tsx">
      <Icon size="sm" fill={ICON_COLOR.neutral} className="shrink-0" data-sentry-element="Icon" data-sentry-source-file="ProfileSidebar.tsx" />
      <span className="min-w-0 flex-1 truncate text-left">{menu.label}</span>
    </Button>;
};